/**
 * Collection Page Engraving Preview
 * Shows the customer's stored engraving text on product cards.
 * Uses the same circular arc algorithm and per-curve presets as the product page.
 */
(function() {
  'use strict';

  if (window.__engravingPreviewInit) return;
  window.__engravingPreviewInit = true;

  const STORAGE_KEY = 'ld_engraving';
  const EXPIRY_MS = 48 * 60 * 60 * 1000;
  const ICON_CHARS = new Set(['/', '(', ')', '<', '>', '^', '[', ']']);
  const SYMBOLER_FONTS = ['LD-Standard', 'LD-ComicSans', 'LD-Dancing', 'LD-Arial'];
  const SAFE_FONT = 'Arial, Helvetica, sans-serif';
  const DEFAULT_MAX_TEXT_WIDTH = 90;

  function fontHasIcons(cssStack) {
    return SYMBOLER_FONTS.some(f => (cssStack || '').includes(f));
  }

  const CURVE_PRESETS = {
    round: {
      positionY: 20.5, centerX: 77, diameter: 71, fontSize: 9,
      letterSpacing: 1, line2Offset: 4.5, fontWeight: 400, opacity: 0.8,
      maxTextWidth: 43,
      twoLine: { diamMul: 1, l2offMul: 0.9 }
    },
    butterfly: {
      positionY: 40.5, centerX: 76, diameter: 300, fontSize: 8.5,
      letterSpacing: 0.4, line2Offset: 3.5, fontWeight: 400, opacity: 0.55,
      straight: true, maxTextWidth: 43,
      twoLine: { diamMul: 2, l2offMul: 1 }
    },
    flat: {
      positionY: 45, centerX: 74, diameter: 300, fontSize: 8.5,
      letterSpacing: 0.4, line2Offset: 3, fontWeight: 400, opacity: 0.55,
      straight: true, maxTextWidth: 45,
      twoLine: { diamMul: 2, l2offMul: 1 }
    },
    lucky: {
      positionY: 45, centerX: 74, diameter: 300, fontSize: 8.5,
      letterSpacing: 0.4, line2Offset: 3, fontWeight: 400, opacity: 0.55,
      straight: true, maxTextWidth: 45,
      twoLine: { diamMul: 2, l2offMul: 1 }
    }
  };

  let measureCtx;
  function measureCharWidths(text, fontSize, fontFamily) {
    if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
    const REF = 100;
    measureCtx.font = `400 ${REF}px ${fontFamily || 'sans-serif'}`;
    const scale = fontSize / REF;
    return [...text].map(ch => measureCtx.measureText(ch).width * scale);
  }

  function measureCharWidthsForDisplay(text, fontSize, fontFamily) {
    if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
    const REF = 100;
    const scale = fontSize / REF;
    return [...text].map(ch => {
      const font = (ICON_CHARS.has(ch) && fontHasIcons(fontFamily)) ? fontFamily : (fontHasIcons(fontFamily) ? SAFE_FONT : fontFamily);
      measureCtx.font = `400 ${REF}px ${font || 'sans-serif'}`;
      return measureCtx.measureText(ch).width * scale;
    });
  }

  function calcDynamicFontSize(textLength, base) {
    if (textLength <= 1) return base * 1.35;
    if (textLength <= 3) return base * 1.2;
    if (textLength <= 6) return base * 1.1;
    if (textLength <= 9) return base;
    if (textLength <= 12) return base * 0.9;
    return base * 0.8;
  }

  function placeCharsOnArc(text, fontSize, diameter, kerning, centerX, arcCenterY, lineOffset, fontFamily) {
    if (!text || text.length === 0) return [];
    const R = diameter / 2;
    const spacingR = R - fontSize;
    const baseR = R - fontSize / 2;
    const posR = baseR - lineOffset;
    if (spacingR <= 0 || posR <= 0) return [];

    const charWidths = measureCharWidthsForDisplay(text, fontSize, fontFamily);
    let totalAngle = 0;
    for (let i = 0; i < text.length; i++) {
      totalAngle += charWidths[i] / spacingR;
      if (i < text.length - 1) totalAngle += kerning / spacingR;
    }

    let angle = -totalAngle / 2;
    const chars = [];
    for (let i = 0; i < text.length; i++) {
      const halfCharAngle = (charWidths[i] / 2) / spacingR;
      angle += halfCharAngle;
      chars.push({
        char: text[i],
        x: Math.round((centerX + posR * Math.sin(angle)) * 100) / 100,
        y: Math.round((arcCenterY - posR * Math.cos(angle)) * 100) / 100,
        rotation: Math.round(angle * (180 / Math.PI) * 100) / 100,
        isIcon: ICON_CHARS.has(text[i])
      });
      angle += (charWidths[i] / 2 + kerning) / spacingR;
    }
    return chars;
  }

  function placeCharsStraight(text, fontSize, kerning, centerX, centerY, lineOffset, fontFamily) {
    if (!text || text.length === 0) return [];
    const charWidths = measureCharWidthsForDisplay(text, fontSize, fontFamily);
    const totalW = charWidths.reduce((s, w) => s + w, 0) + (text.length - 1) * kerning;
    let x = centerX - totalW / 2;
    const y = centerY + lineOffset;
    const chars = [];
    for (let i = 0; i < text.length; i++) {
      x += charWidths[i] / 2;
      chars.push({
        char: text[i],
        x: Math.round(x * 100) / 100,
        y: Math.round(y * 100) / 100,
        rotation: 0,
        isIcon: ICON_CHARS.has(text[i])
      });
      x += charWidths[i] / 2 + kerning;
    }
    return chars;
  }

  function getStoredEngraving() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (Date.now() - data.timestamp > EXPIRY_MS) return null;
      const fullText = data.text || ((data.text1 || '') + (data.text2 || ''));
      if (!fullText.trim()) return null;
      return { text: fullText, font: data.font || '', cssStack: data.cssStack || '' };
    } catch (e) {
      return null;
    }
  }

  function buildSVG(curveHandle, line1, line2, fontFamily) {
    const preset = CURVE_PRESETS[curveHandle] || CURVE_PRESETS.round;
    const hasLine2 = line2.length > 0;

    let diam = preset.diameter;
    let l2off = preset.line2Offset;
    const kern = preset.letterSpacing;

    if (hasLine2) {
      diam *= preset.twoLine.diamMul;
      l2off *= preset.twoLine.l2offMul;
    }

    const baseR = diam / 2 - preset.fontSize / 2;
    const arcCenterY = preset.positionY + baseR;

    const maxLen = Math.max(line1.length, line2.length || 0);
    let fontSize = calcDynamicFontSize(maxLen, preset.fontSize);

    // Auto-scale font if text exceeds max width
    for (let attempt = 0; attempt < 5; attempt++) {
      const testWidths = measureCharWidthsForDisplay(line1 || line2, fontSize, fontFamily);
      const totalW = testWidths.reduce((s, w) => s + w, 0) + Math.max(0, testWidths.length - 1) * kern;
      const mtw = preset.maxTextWidth || DEFAULT_MAX_TEXT_WIDTH;
      if (totalW <= mtw) break;
      fontSize *= (mtw / totalW);
    }

    let line1Chars, line2Chars;
    if (preset.straight) {
      line1Chars = placeCharsStraight(line1, fontSize, kern, preset.centerX, preset.positionY, hasLine2 ? -l2off : 0, fontFamily);
      line2Chars = placeCharsStraight(line2, fontSize, kern, preset.centerX, preset.positionY, l2off, fontFamily);
    } else {
      line1Chars = placeCharsOnArc(line1, fontSize, diam, kern, preset.centerX, arcCenterY, hasLine2 ? -l2off : 0, fontFamily);
      line2Chars = placeCharsOnArc(line2, fontSize, diam, kern, preset.centerX, arcCenterY, l2off, fontFamily);
    }

    const hasIcons = fontHasIcons(fontFamily);
    const iconFB = "'LD-ComicSans'";

    function charSvg(ch) {
      const displayFont = ch.isIcon ? (hasIcons ? fontFamily : iconFB) : (hasIcons ? SAFE_FONT : fontFamily);
      const escaped = (displayFont || '').replace(/"/g, '&quot;');
      const ff = `font-family="${escaped}"`;
      return `<text x="0" y="0" transform="translate(${ch.x},${ch.y}) rotate(${ch.rotation})" font-size="${fontSize}" font-weight="${preset.fontWeight}" fill="${preset.color || '#313131'}" opacity="${preset.opacity}" text-anchor="middle" dominant-baseline="central" ${ff}>${escapeHtml(ch.char)}</text>`;
    }

    return `<svg viewBox="0 0 150 100" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;display:block;">
      <g>${line1Chars.map(charSvg).join('')}</g>
      <g>${line2Chars.map(charSvg).join('')}</g>
    </svg>`;
  }

  function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function applyPreviews() {
    const stored = getStoredEngraving();
    if (!stored) return;

    const maxChars = 12;
    const line1 = stored.text.substring(0, maxChars);
    const line2 = stored.text.substring(maxChars, maxChars * 2);
    const fontFamily = stored.cssStack || 'LD-Standard, Arial, sans-serif';

    document.querySelectorAll('[data-engraving-card]').forEach(function(el) {
      const handle = el.dataset.curveHandle || 'round';
      el.innerHTML = buildSVG(handle, line1, line2, fontFamily);
      el.style.display = '';
    });
  }

  function run() {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(applyPreviews);
    } else {
      applyPreviews();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  window.addEventListener('storage', function(e) {
    if (e.key === STORAGE_KEY) applyPreviews();
  });
})();
